import { useEffect, useRef, useState } from 'react';

const VIEW_BOX = '0 0 100 128';

const ARM_LEFT = 'M 20 0 L 50 42 L 44 48 L 0 4 Z';
const ARM_RIGHT = 'M 80 0 L 100 4 L 56 48 L 50 42 Z';
const ARM_STEM = 'M 44 48 L 56 48 L 56 108 L 50 128 L 44 108 Z';

const ANIM_DURATION_MS = 6200;
const AUTO_INTERVAL_MS = 10 * 60 * 1000;

const EVT = 'tryggr:logo-animate';

let __schedulerStarted = false;
function startScheduler() {
  if (__schedulerStarted || typeof window === 'undefined') return;
  __schedulerStarted = true;
  setInterval(() => {
    window.dispatchEvent(new CustomEvent(EVT));
  }, AUTO_INTERVAL_MS);
}

let __filterId = 0;
function nextFilterId() {
  __filterId += 1;
  return `tryggr-goo-${__filterId}`;
}

function AnimatedSvg({ size, playing, fill }) {
  const filterId = useRef(nextFilterId()).current;
  const cls = playing ? 'tryggr-anim-running' : '';
  return (
    <svg
      width={size}
      height={size}
      viewBox={VIEW_BOX}
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }}
      className={cls}
    >
      <defs>
        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation="0"
            result="blur"
            className="tryggr-goo-blur"
          >
            <animate
              attributeName="stdDeviation"
              values="0;0;3.5;6;6;3.5;0;0"
              keyTimes="0;0.18;0.32;0.5;0.62;0.78;0.9;1"
              dur={`${ANIM_DURATION_MS}ms`}
              begin="indefinite"
              fill="freeze"
              repeatCount="1"
              id={`${filterId}-anim`}
            />
          </feGaussianBlur>
          <feColorMatrix
            in="blur"
            mode="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    0 0 0 22 -10"
            result="goo"
          />
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </defs>
      <g filter={`url(#${filterId})`} className="tryggr-arms">
        <path d={ARM_LEFT} fill={fill} className="tryggr-arm tryggr-arm-left" />
        <path d={ARM_RIGHT} fill={fill} className="tryggr-arm tryggr-arm-right" />
        <path d={ARM_STEM} fill={fill} className="tryggr-arm tryggr-arm-stem" />
      </g>
    </svg>
  );
}

export default function AnimatedTryggrLogo({ size = 52, borderRadius = 14, style = {} }) {
  const [playing, setPlaying] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    startScheduler();
  }, []);

  const triggerAnim = () => {
    if (playing) return;
    setPlaying(true);
    const root = wrapperRef.current;
    if (root) {
      const anims = root.querySelectorAll('animate');
      anims.forEach((a) => {
        try {
          a.beginElement();
        } catch (_) {
          /* ignore */
        }
      });
    }
    setTimeout(() => setPlaying(false), ANIM_DURATION_MS);
  };

  useEffect(() => {
    const handler = () => triggerAnim();
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  return (
    <>
      <style>{STYLES}</style>
      <div
        ref={wrapperRef}
        onMouseEnter={triggerAnim}
        style={{
          width: size,
          height: size,
          borderRadius,
          background: 'linear-gradient(135deg, var(--accent), #38bdf8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...style,
        }}
      >
        <div style={{ width: size * 0.5, height: size * 0.5 }}>
          <AnimatedSvg size={size * 0.5} playing={playing} fill="#0d1f1e" />
        </div>
      </div>
    </>
  );
}

const STYLES = `
.tryggr-arm {
  transform-box: fill-box;
  transform-origin: 50% 100%;
}

.tryggr-anim-running .tryggr-arm-left {
  animation: tryggr-left ${ANIM_DURATION_MS}ms cubic-bezier(.45,.05,.55,.95) 1;
}
.tryggr-anim-running .tryggr-arm-right {
  animation: tryggr-right ${ANIM_DURATION_MS}ms cubic-bezier(.45,.05,.55,.95) 1;
}
.tryggr-anim-running .tryggr-arm-stem {
  animation: tryggr-stem ${ANIM_DURATION_MS}ms cubic-bezier(.45,.05,.55,.95) 1;
  transform-origin: 50% 0%;
}

@keyframes tryggr-left {
  0%   { transform: translate(0,0) rotate(0deg); }
  18%  { transform: translate(0,0) rotate(0deg); }
  32%  { transform: translate(-6px,-4px) rotate(-8deg); }
  50%  { transform: translate(-10px,2px) rotate(-14deg) scale(1.05); }
  62%  { transform: translate(-7px,6px) rotate(-6deg) scale(1.02); }
  78%  { transform: translate(-3px,2px) rotate(-2deg); }
  90%  { transform: translate(0.5px,0.5px) rotate(0.5deg); }
  100% { transform: translate(0,0) rotate(0deg); }
}
@keyframes tryggr-right {
  0%   { transform: translate(0,0) rotate(0deg); }
  18%  { transform: translate(0,0) rotate(0deg); }
  32%  { transform: translate(6px,-4px) rotate(8deg); }
  50%  { transform: translate(10px,2px) rotate(14deg) scale(1.05); }
  62%  { transform: translate(7px,6px) rotate(6deg) scale(1.02); }
  78%  { transform: translate(3px,2px) rotate(2deg); }
  90%  { transform: translate(-0.5px,0.5px) rotate(-0.5deg); }
  100% { transform: translate(0,0) rotate(0deg); }
}
@keyframes tryggr-stem {
  0%   { transform: translate(0,0) rotate(0deg); }
  18%  { transform: translate(0,0) rotate(0deg); }
  32%  { transform: translate(0,8px) rotate(0deg); }
  50%  { transform: translate(0,12px) scale(1, 0.92); }
  62%  { transform: translate(0,8px) scale(1, 0.96); }
  78%  { transform: translate(0,3px) scale(1, 0.99); }
  90%  { transform: translate(0,0.5px); }
  100% { transform: translate(0,0); }
}
`;
